import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { DashboardLayout } from "@/layouts/DashboardLayout";
import { Loading } from "@/components";

// Eager: marketing + auth (fast first paint)
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import VerifyEmail from "@/pages/VerifyEmail";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";

// Lazy: every feature module is code-split into its own chunk
const OverviewPage = lazy(() => import("@/features/dashboard/OverviewPage"));
const AdminOverviewPage = lazy(() => import("@/features/dashboard/AdminOverviewPage"));
const UsersPage = lazy(() => import("@/features/users/UsersPage"));
const RolesPage = lazy(() => import("@/features/roles/RolesPage"));
const CompaniesPage = lazy(() => import("@/features/companies/CompaniesPage"));
const SettingsPage = lazy(() => import("@/features/settings/SettingsPage"));
const ReceiptDesignPage = lazy(() => import("@/features/settings/ReceiptDesignPage"));
const BillingPage = lazy(() => import("@/features/billing/BillingPage"));
const BookingsPage = lazy(() => import("@/features/bookings/BookingsPage"));
const BookingSettingsPage = lazy(() => import("@/features/booking-settings/BookingSettingsPage"));
const CustomersPage = lazy(() => import("@/features/crm/CustomersPage"));
const WorkOrdersPage = lazy(() => import("@/features/workorders/WorkOrdersPage"));
const InventoryPage = lazy(() => import("@/features/inventory/InventoryPage"));
const InvoicesPage = lazy(() => import("@/features/invoices/InvoicesPage"));
const Placeholder = lazy(() => import("@/pages/Placeholder"));
const CommunicationCenterPage = lazy(() => import("@/features/communications/CommunicationCenterPage"));
const TrackPage = lazy(() => import("@/pages/TrackPage"));
const PublicQuotePage = lazy(() => import("@/features/public/PublicQuotePage"));
const PublicSignPage = lazy(() => import("@/features/public/PublicSignPage"));
const PublicBookingPage = lazy(() => import("@/features/public/PublicBookingPage"));
const DefaultBookingPage = lazy(() => import("@/features/public/DefaultBookingPage"));
const ManageBookingPage = lazy(() => import("@/features/public/ManageBookingPage"));
const CustomerPortalPage = lazy(() => import("@/features/portal/CustomerPortalPage"));
const PortalPayReturn = lazy(() => import("@/features/portal/PortalPayReturn"));

const COMPANY_PLACEHOLDERS = [
  "webshop", "reports", "ai", "support",
];
const ADMIN_PLACEHOLDERS = ["plans", "logs", "settings"];

export default function App() {
  return (
    <Suspense fallback={<Loading label="Laddar modul…" className="min-h-screen" />}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/book/:slug" element={<PublicBookingPage />} />
        <Route path="/boka" element={<DefaultBookingPage />} />
        <Route path="/boka/hantera/:token" element={<ManageBookingPage />} />
        <Route path="/portal/:slug/pay-return" element={<PortalPayReturn />} />
        <Route path="/portal/:slug" element={<CustomerPortalPage />} />
        <Route path="/track/:slug/:number" element={<TrackPage />} />
        <Route path="/offert/:token" element={<PublicQuotePage />} />
        <Route path="/signera/:token" element={<PublicSignPage />} />

        <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout variant="company" /></ProtectedRoute>}>
          <Route index element={<OverviewPage />} />
          <Route path="bookings" element={<BookingsPage />} />
          <Route path="booking-settings" element={<BookingSettingsPage />} />
          <Route path="customers" element={<CustomersPage />} />
          <Route path="workorders" element={<WorkOrdersPage />} />
          <Route path="inventory" element={<InventoryPage />} />
          <Route path="invoices" element={<InvoicesPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="roles" element={<RolesPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="settings/receipt-design" element={<ReceiptDesignPage />} />
          <Route path="billing" element={<BillingPage />} />
          <Route path="communication" element={<CommunicationCenterPage />} />
          {COMPANY_PLACEHOLDERS.map((p) => (
            <Route key={p} path={p} element={<Placeholder />} />
          ))}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Route>

        <Route path="/admin" element={<ProtectedRoute requireSuperAdmin><DashboardLayout variant="admin" /></ProtectedRoute>}>
          <Route index element={<AdminOverviewPage />} />
          <Route path="tenants" element={<CompaniesPage />} />
          {ADMIN_PLACEHOLDERS.map((p) => (
            <Route key={p} path={p} element={<Placeholder />} />
          ))}
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
